/**
 * useDataQualityReport hook — encapsulates all Data Quality API calls.
 *
 * Endpoints wired (all called in parallel):
 * - POST /api/v1/data-quality/validate — Pandera schema validation
 * - POST /api/v1/data-quality/expectations — Great Expectations business rules
 * - POST /api/v1/data-quality/profile — Statistical profiling
 * - POST /api/v1/data-quality/cross-validate — Cross-document validation
 * - POST /api/v1/data-quality/report — Comprehensive quality report
 *
 * Design constraints:
 * - Calls all 5 endpoints in parallel for a given claimId
 * - Returns loading/error states
 * - Does NOT use React Query (follows codebase pattern like useCopilot)
 * - Existing DataQualityWidget and its hook remain untouched
 */

import { useState, useCallback } from 'react';
import {
  fetchDataQualityValidate,
  fetchExpectations,
  fetchProfile,
  fetchCrossValidate,
  fetchDataQualityReport,
  ValidateResult,
  GXValidateResult,
  ProfileResult,
  CrossValidateResult,
  DataQualityReport,
} from '@/lib/api/dataQualityApi';

// =============================================================================
// TYPES
// =============================================================================

export interface DataQualityReportState {
  /** Pandera schema validation result */
  validation: ValidateResult | null;
  /** Great Expectations validation result */
  expectations: GXValidateResult | null;
  /** Statistical profiling result */
  profile: ProfileResult | null;
  /** Cross-document validation result */
  crossValidation: CrossValidateResult | null;
  /** Comprehensive quality report */
  report: DataQualityReport | null;
  /** Whether any fetch is in progress */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

const initialState: DataQualityReportState = {
  validation: null,
  expectations: null,
  profile: null,
  crossValidation: null,
  report: null,
  isLoading: false,
  error: null,
};

export function useDataQualityReport() {
  const [state, setState] = useState<DataQualityReportState>(initialState);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  /**
   * Fetch all data quality reports in parallel for a given claimId.
   * Calls all 5 endpoints: validate, expectations, profile, cross-validate, report.
   */
  const fetchAll = useCallback(async (claimId: string): Promise<boolean> => {
    if (!claimId) {
      setState((prev) => ({
        ...prev,
        error: new Error('claimId is required'),
      }));
      return false;
    }

    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      // Call all 5 endpoints in parallel
      const [
        validation,
        expectations,
        profile,
        crossValidation,
        report,
      ] = await Promise.all([
        fetchDataQualityValidate(claimId).catch((err) => {
          console.warn('Validation fetch failed:', err);
          return null;
        }),
        fetchExpectations(claimId).catch((err) => {
          console.warn('Expectations fetch failed:', err);
          return null;
        }),
        fetchProfile(claimId).catch((err) => {
          console.warn('Profile fetch failed:', err);
          return null;
        }),
        fetchCrossValidate(claimId).catch((err) => {
          console.warn('Cross-validation fetch failed:', err);
          return null;
        }),
        fetchDataQualityReport(claimId).catch((err) => {
          console.warn('Report fetch failed:', err);
          return null;
        }),
      ]);

      setState({
        validation,
        expectations,
        profile,
        crossValidation,
        report,
        isLoading: false,
        error: null,
      });

      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error,
      }));
      return false;
    }
  }, []);

  /**
   * Refetch all data quality reports.
   * Alias for fetchAll for clarity in consuming components.
   */
  const refetch = useCallback(
    async (claimId: string): Promise<boolean> => fetchAll(claimId),
    [fetchAll]
  );

  return {
    // Results
    validation: state.validation,
    expectations: state.expectations,
    profile: state.profile,
    crossValidation: state.crossValidation,
    report: state.report,
    // Loading state
    isLoading: state.isLoading,
    // Error state
    error: state.error,
    clearError,
    // Actions
    fetchAll,
    refetch,
    reset,
  };
}

export default useDataQualityReport;
